'use client';

import { useEffect, useState } from 'react';
import {
  PLAN_BILLING_PERIODS,
  PLAN_KIND_LABELS,
  type PlanKind,
} from '@/core/domain';
import { Button } from '@/core/presentation/components/ui/Button';
import { Input } from '@/core/presentation/components/ui/Input';
import { Label } from '@/core/presentation/components/ui/Label';
import { Switch } from '@/core/presentation/components/ui/Switch';
import { useToast } from '@/core/presentation/components/Toast';
import { usePlans } from '@/features/plans/presentation/context/PlansContext';
import { PlanBillingVariantFields } from '@/features/plans/presentation/admin/components/PlanBillingVariantFields';
import {
  PLAN_ADMIN_ACTIVE_LABEL,
  PLAN_ADMIN_BILLING_SECTION_ARIA,
  PLAN_ADMIN_CREATE_TITLE,
  PLAN_ADMIN_EDIT_TITLE,
  PLAN_ADMIN_KIND_FREE_HELP,
  PLAN_ADMIN_KIND_LABEL,
  PLAN_ADMIN_KIND_PAID_HELP,
  PLAN_ADMIN_FREE_LIMITS_SECTION,
  PLAN_ADMIN_FREE_MAX_SERVICES_HELP,
  PLAN_ADMIN_FREE_MAX_SERVICES_LABEL,
  PLAN_ADMIN_SORT_LABEL,
  PLAN_ADMIN_TITLE_LABEL,
  PLAN_ADMIN_TITLE_PLACEHOLDER,
  PLAN_ADMIN_TOAST_CREATED,
  PLAN_ADMIN_TOAST_UPDATED,
} from '@/features/plans/presentation/composables/constants/plan-admin.constants';
import {
  buildPlanFromForm,
  emptyPlanForm,
  planToFormState,
  type PlanFormState,
} from '@/features/plans/presentation/admin/utils/plan-form';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface PlanFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  planId: string | null;
  initialKind?: PlanKind;
}

export function PlanFormModal({
  open,
  onOpenChange,
  planId,
  initialKind = 'paid',
}: PlanFormModalProps) {
  const toast = useToast();
  const {
    state: { plans, loading },
    events,
  } = usePlans();
  const [form, setForm] = useState<PlanFormState>(() => emptyPlanForm(initialKind));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (planId) {
      const existing = plans.find((p) => p.id === planId);
      if (existing) {
        setForm(planToFormState(existing));
      }
      return;
    }
    setForm(emptyPlanForm(initialKind));
  }, [open, planId, plans, initialKind]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  if (!open) return null;

  const title = planId ? PLAN_ADMIN_EDIT_TITLE : PLAN_ADMIN_CREATE_TITLE;
  const isEdit = Boolean(planId);
  const busy = loading || submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const existingPlan = planId ? plans.find((p) => p.id === planId) ?? null : null;
    const { plan, error } = buildPlanFromForm(form, existingPlan);
    if (!plan || error) {
      toast.error(error ?? 'Revise os campos do formulário.');
      return;
    }

    setSubmitting(true);
    if (planId) {
      const { id, created_at: _c, ...patch } = plan;
      const result = await events.update(id, patch);
      setSubmitting(false);
      if (result.success) {
        toast.success(PLAN_ADMIN_TOAST_UPDATED);
        onOpenChange(false);
      } else if (result.error) {
        toast.error(result.error);
      }
      return;
    }

    const result = await events.create(plan);
    setSubmitting(false);
    if (result.success) {
      toast.success(PLAN_ADMIN_TOAST_CREATED);
      onOpenChange(false);
    } else if (result.error) {
      toast.error(result.error);
    }
  };

  const setVariant = (
    period: (typeof PLAN_BILLING_PERIODS)[number],
    slice: PlanFormState['variants'][typeof period]
  ) => {
    setForm((f) => ({
      ...f,
      variants: { ...f.variants, [period]: slice },
    }));
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/50"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="plan-form-title"
        className="w-full max-w-lg rounded-xl border border-admin-border bg-white shadow-lg max-h-[90vh] overflow-y-auto"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-admin-border px-4 py-3">
          <h2 id="plan-form-title" className="text-lg font-semibold text-admin-text">
            {title}
          </h2>
          <button
            type="button"
            className="rounded-lg p-1.5 text-admin-text-secondary hover:bg-admin-bg-block hover:text-admin-text"
            aria-label="Fechar"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {!isEdit ?
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium text-admin-text">{PLAN_ADMIN_KIND_LABEL}</legend>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {(['free', 'paid'] as const).map((kind) => (
                  <label
                    key={kind}
                    className={cn(
                      'flex cursor-pointer flex-col rounded-lg border px-3 py-2.5 transition-colors',
                      form.kind === kind ?
                        'border-admin-cyan bg-admin-cyan/10'
                      : 'border-admin-border hover:bg-admin-bg-block'
                    )}
                  >
                    <span className="flex items-center gap-2 text-sm font-semibold text-admin-text">
                      <input
                        type="radio"
                        name="plan-kind"
                        value={kind}
                        checked={form.kind === kind}
                        disabled={busy}
                        className="accent-admin-cyan"
                        onChange={() => setForm(emptyPlanForm(kind))}
                      />
                      {PLAN_KIND_LABELS[kind]}
                    </span>
                    <span className="mt-1 text-xs text-admin-text-secondary leading-relaxed">
                      {kind === 'free' ? PLAN_ADMIN_KIND_FREE_HELP : PLAN_ADMIN_KIND_PAID_HELP}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          : (
            <p className="text-sm text-admin-text-secondary">
              Tipo:{' '}
              <span className="font-semibold text-admin-text">{PLAN_KIND_LABELS[form.kind]}</span>
            </p>
          )}

          <div>
            <Label htmlFor="plan-title" className="text-admin-text">
              {PLAN_ADMIN_TITLE_LABEL}
            </Label>
            <Input
              id="plan-title"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              className="mt-1 border-admin-border"
              variant="light"
              disabled={busy}
              placeholder={PLAN_ADMIN_TITLE_PLACEHOLDER}
              required
            />
          </div>

          <div>
            <Label htmlFor="plan-sort" className="text-admin-text">
              {PLAN_ADMIN_SORT_LABEL}
            </Label>
            <Input
              id="plan-sort"
              type="number"
              min={0}
              step={1}
              value={form.sort_order}
              onChange={(e) =>
                setForm((f) => ({ ...f, sort_order: Number(e.target.value) || 0 }))
              }
              className="mt-1 border-admin-border"
              variant="light"
              disabled={busy}
            />
          </div>

          {form.kind === 'free' ?
            <div
              className="space-y-3 rounded-lg border border-admin-border bg-admin-bg-block px-3 py-3"
              role="group"
              aria-label={PLAN_ADMIN_FREE_LIMITS_SECTION}
            >
              <p className="text-sm font-medium text-admin-text">{PLAN_ADMIN_FREE_LIMITS_SECTION}</p>
              <div>
                <Label htmlFor="plan-max-services" className="text-admin-text">
                  {PLAN_ADMIN_FREE_MAX_SERVICES_LABEL}
                </Label>
                <p className="mt-0.5 text-xs text-admin-text-secondary leading-relaxed">
                  {PLAN_ADMIN_FREE_MAX_SERVICES_HELP}
                </p>
                <Input
                  id="plan-max-services"
                  type="number"
                  min={0}
                  step={1}
                  value={form.max_services}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      max_services: Number(e.target.value) || 0,
                    }))
                  }
                  className="mt-1.5 border-admin-border"
                  variant="light"
                  disabled={busy}
                  required
                />
              </div>
            </div>
          : null}

          {form.kind === 'paid' ?
            <div className="space-y-2" role="group" aria-label={PLAN_ADMIN_BILLING_SECTION_ARIA}>
              <p className="text-sm font-medium text-admin-text">Periodicidades (preço + Stripe)</p>
              {PLAN_BILLING_PERIODS.map((period, index) => (
                <PlanBillingVariantFields
                  key={period}
                  period={period}
                  value={form.variants[period]}
                  onChange={(slice) => setVariant(period, slice)}
                  disabled={busy}
                  defaultOpen={index === 0}
                />
              ))}
            </div>
          : null}

          <div className="flex items-center justify-between rounded-lg border border-admin-border bg-admin-bg-block px-3 py-2 gap-3">
            <span className="text-sm text-admin-text">{PLAN_ADMIN_ACTIVE_LABEL}</span>
            <Switch
              checked={form.is_active}
              onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))}
              disabled={busy}
              className="shrink-0"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="flex-1 border-admin-border text-admin-text"
              disabled={busy}
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              size="sm"
              className="flex-1 bg-admin-navy hover:bg-admin-navy-mid text-white"
              disabled={busy}
              loading={submitting}
              loadingLabel="Salvando"
            >
              {isEdit ? 'Salvar' : 'Criar'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
